import { DestroyRef, Injectable, type Signal, computed, effect, inject, signal, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { EMPTY, Subscription, catchError, exhaustMap, finalize, timeout, timer, interval, tap } from 'rxjs';
import { ProjectsFacade } from './projects.facade';
import { PhotoUploadCoordinatorService } from './photo-upload-coordinator.service';
import { ImportWaveOutcomeService } from './import-wave-outcome.service';
import { ProjectsApiService } from '../infrastructure/projects-api.service';
import { PersonImportApiService, isDefiniteOff, type ImportRun } from '../infrastructure/person-import-api.service';
import {
  isClusteringPending,
  isFacePathDone,
  isProcessingGateReady,
  isTerminalProcessStatus,
  type ProjectProcessStatus,
} from '../entities/project.models';
import { withTimeout } from '../utils/with-timeout.util';

/** Structurally matches the dropzone step's PhotosProcessingGate input. */
export interface ProcessingGateView {
  readonly state: 'recognizing' | 'clustering' | 'naming' | 'ready';
  /** Готовность шага в процентах, 0–100. */
  readonly progress: number;
  /** Ничего не менялось дольше порога: показать «дольше обычного» вместо неизменного спиннера. */
  readonly stalled?: boolean;
  /** Нечем мерить (в снимке нет счётчиков цикла): кольцо крутится без числа. */
  readonly indeterminate?: boolean;
}

const POLL_INTERVAL_MS = 10_000;
// Generous on purpose: the timeout must only reap a genuinely hung request, never a slow-but-live
// response (staging measured up to ~52s under load) — aborting a response that would land re-starves the gate.
const POLL_TIMEOUT_MS = 60_000;
// Досчёт волн имён — та же логика предохранителя: транспортный отказ обязан ретраиться (иначе шаг
// откроется без имён, ради которых он ждёт), но не бесконечно — после капа гейт открывается, а
// незакрытые волны дожимает крон, как и раньше.
const SETTLE_ATTEMPT_CAP = 3;
// Потолок одного досчёта — тот же, что у опроса: settle большой волны легитимно долгий, а зависший
// запрос без потолка глушил бы ретрай (попытка «уже идёт») и не считался бы в кап. Таймаут —
// транспортный отказ: та же ветка, тот же кап.
const SETTLE_TIMEOUT_MS = POLL_TIMEOUT_MS;
// Догрузка ростера — та же ветка, что и досчёт: висящий промис без потолка держал «попытку уже идёт»
// и не считался в кап, а вместе с ним стоял и гейт. Инцидент 10.09: 47 минут на закрытом проекте.
const PREFETCH_ATTEMPT_CAP = SETTLE_ATTEMPT_CAP;
const PREFETCH_TIMEOUT_MS = SETTLE_TIMEOUT_MS;
// Предохранитель забора цикла — любой клиентский забор обязан иметь выход. Забор ждёт, чтобы бэкенд
// видимо подхватил работу: новый цикл либо тот же цикл, но нетерминальный. Три случая ни того, ни
// другого не дают, и без срока каждый запирал бы «Продолжить» навсегда: пачка, у которой ничего не
// легло на сервер (все файлы — дубли или обрыв передачи); принятый на хвосте загрузки файл, чей
// fileProcess оркестратор заводит через Kafka позже HTTP 201, когда все предыдущие уже обработаны и
// снимок «тот же цикл, уже processed» (FKS-12647); слияние или перенос кадра, которые цикла не рождают.
// 30 с покрывают TTL кэша статуса (15 с) и лаг Kafka до оркестратора с запасом вдвое; лаг больше —
// уже инцидент оркестратора, и в нём остаток окна потери имён принят осознанно.
const CYCLE_FENCE_MAX_MS = 30_000;
// Заслонка пересбора персон: держит «Продолжить», пока сервер удаляет и заново заводит персон в том же
// цикле. Снимается первой догрузкой ростера с учениками либо по сроку.
// ponytail: фиксированный кап; заменить серверным флагом «reseeding», если срока не хватит.
const RESEED_FENCE_MAX_MS = 60_000;
const reseedStorageKey = (projectId: string): string => `reseed:${projectId}`;
// Щеколда повтора пересбора: запрос не ушёл, а перезагрузка сама его не повторяет — без неё проект
// остаётся с нулём персон и без выхода. Живёт рядом со сроком заслонки, в том же sessionStorage.
const reseedRetryStorageKey = (projectId: string): string => `reseed-retry:${projectId}`;
// Зеркало щеколды в памяти вкладки на случай закрытого хранилища — модульное, как memoryMarks у метки
// рождения проекта: одно на вкладку, общее для всех инстансов гейта (сервис провайдится компонентом),
// ровно семантика ключа в sessionStorage. Перезагрузку, как и память вкладки, не переживает.
const reseedRetryLatch = new Set<string>();

/** Для спек: модульное зеркало переживает TestBed, а сценарии одной спеки не должны течь друг в друга. */
export function resetReseedRetryLatch(): void {
  reseedRetryLatch.clear();
}

// Срок вышел, а персон всё нет: алерт просит обновить страницу — значит и он сам, и заслонка под ним
// обязаны пережить обновление. Без метки экран после перезагрузки снова врал бы красным «Нужны
// одиночные портреты».
const reseedOverdueStorageKey = (projectId: string): string => `reseed-overdue:${projectId}`;
// Прогон волны, который сервер уже закрыл; всё остальное (PREVIEW/APPLYING/PARTIAL) — ещё в работе.
export const SETTLED_RUN_STATUSES: ReadonlySet<ImportRun['status']> = new Set<ImportRun['status']>(['APPLIED', 'FAILED']);
// Другой вопрос и потому другое множество: прогон, который УЖЕ дописал имена в персон. PARTIAL сюда
// входит (apply прошёл, остаток файлов ждёт распознавания) — гейту он значит «дожимай ещё», а тому,
// кто просто перечитывает ростер, — «имена в базе есть». FAILED не входит: не записано ни одного.
export const NAMES_APPLIED_RUN_STATUSES: ReadonlySet<ImportRun['status']> = new Set<ImportRun['status']>(['APPLIED', 'PARTIAL']);

// Порог считается от ПРОСТОЯ снимка статуса, а не от общего времени ожидания: здоровая массовая
// загрузка двигает счётчики процента непрерывно, и именно это защищает от ложных срабатываний.
// 15 минут без движения процента — тогда подсказка «дольше обычного». Порог не отменяет ожидание —
// он только перестаёт врать, что всё идёт нормально.
const STALL_MS = 15 * 60_000;

// Step percent table — the single FE source: faces 0–55, versions 55–65, persons 65–92, tail 92–99.
const FACES_END = 55;
const VERSIONS_END = 65;
const PERSONS_END = 92;
// The tail is the sole synthetic span (named exception to .claude/rules/single-source-of-truth.md:37):
// past persons the server counts nothing, and a bar parked at 92 reads as hung. Never reaches 99.
const TAIL_PER_SECOND = 0.05;
const TAIL_CAP = 98.9;
// The shown value closes 1/8 of the gap per tick.
const SMOOTHING = 8;
const TERMINAL_CLUSTERING: readonly string[] = ['processed', 'failed'];

/**
 * Orchestrator-status gate for the last photo step before persons check: polls
 * fotoklass-main-process while active and keeps the continue CTA locked until every uploaded
 * photo is processed and face recognition/clustering completes. Component-provided — one
 * instance per wizard page; `start`/`stop` follow the active step.
 */
@Injectable()
export class PhotosProcessingGateService {
  private readonly api = inject(ProjectsApiService);
  private readonly facade = inject(ProjectsFacade);
  private readonly personImport = inject(PersonImportApiService);
  // Сервер объявляет завершение проекта сокетом; без этой подписки гейт узнаёт о нём только на
  // следующем тике опроса, и ускорение бэкенда упирается в десятисекундное окно на фронте.
  private readonly coordinator = inject(PhotoUploadCoordinatorService);
  private stopWatchingCompletion: (() => void) | null = null;
  // Сервис волн даёт страница-хост; гейт живёт и без него (optional), тогда досчёт просто не о ком
  // сообщать — сам запрос всё равно уходит, имена подставляются на сервере.
  private readonly waveOutcome = inject(ImportWaveOutcomeService, { optional: true });

  private readonly projectId = signal<string | null>(null);
  private readonly status = signal<ProjectProcessStatus | null>(null);
  private pendingUploads: Signal<number> | null = null;
  private readonly tick = toSignal(interval(1000).pipe(tap(() => this.stepProgress())), { initialValue: -1 });
  private readonly shownProgress = signal(0);
  private readonly progressKnown = signal(false);
  private progressMayDrop = false;
  // Shown value has nothing to ease from yet (fresh project or new cycle): the first target is adopted as is.
  private progressAdopt = true;
  private progressCycle: string | null = null;
  private progressTotal = 0;
  private tailStartedAt: number | null = null;
  private readonly progressStamp = signal(0);
  private lastProgressFingerprint: string | null = null;
  private readonly personsPrefetchedKey = signal<string | null>(null);
  private readonly settledKey = signal<string | null>(null);
  private settleAttemptKey: string | null = null;
  private settleFailures = 0;
  private prefetchFailures = 0;
  private readonly awaitingCycleAdvanceFrom = signal<string | null | undefined>(undefined);
  /** Забор взвела загрузка (а не мутация персон): только он вправе вести процент к нулю. */
  private fenceByUpload = false;
  /** Срок забора цикла (Date.now()); null — забора нет. */
  private fenceDeadline: number | null = null;
  private prefetchAttemptKey: string | null = null;
  private lastFreshnessKey: string | null = null;
  private lastObservedCycleId: string | null = null;
  private prefetchGeneration = 0;
  private pollGeneration = 0;
  private lastAcceptRestartAt = -Infinity;
  private statusInFlight = false;
  private observedMutationVersion = 0;
  private pollSub: Subscription | null = null;
  private readonly reseedFenceState = signal(false);
  private readonly reseedOverdueState = signal(false);
  private reseedSub: Subscription | null = null;
  /** Идёт пересбор персон: страница держит «Продолжить», пока сервис не снимет заслонку. */
  readonly reseedFence: Signal<boolean> = this.reseedFenceState.asReadonly();
  /** Срок вышел, а ростер с учениками так и не пришёл. Заслонка остаётся стоять: снять её значило бы
   *  объявить, что одиночных портретов нет, — а они есть, и пересбор всё ещё идёт. */
  readonly reseedOverdue: Signal<boolean> = this.reseedOverdueState.asReadonly();

  constructor() {
    inject(DestroyRef).onDestroy(() => this.stop());
    effect(() => {
      const projectId = this.projectId();
      if (!projectId) return;
      const version = this.facade.personsMutationVersionFor(projectId)();
      if (version === this.observedMutationVersion) return;
      untracked(() => this.onPersonsMutation(version, this.facade.lastPersonsMutationRemovedOnly(projectId)));
    });
  }

  private readonly freshnessKey = computed(() => {
    const projectId = this.projectId();
    const status = this.status();
    return projectId && status ? `${projectId}:${status.processingCycleId ?? 'legacy'}` : null;
  });

  // Ключ догрузки ростера: цикл + живой счётчик персон + число файлов. Пересбор персон цикла не меняет,
  // и по одному ключу свежести ростер догружался бы один раз — на старом составе; счётчик в ключе делает
  // новую догрузку обязательной, а ответ старой — устаревшим. Лёгкий снимок статуса счётчика персон не
  // несёт вовсе — из другой вкладки удаление кадров видно только по числу файлов: без него ростер на
  // шаге сверки так и стоял бы на удалённых людях.
  private readonly prefetchKey = computed(() => {
    const key = this.freshnessKey();
    const status = this.status();
    return key ? `${key}:${status?.countCreatedPersons ?? 'none'}:${status?.countFiles ?? 'none'}` : null;
  });

  readonly ready = computed(() => {
    const key = this.freshnessKey();
    return (
      key !== null &&
      this.awaitingCycleAdvanceFrom() === undefined &&
      isProcessingGateReady(this.status()) &&
      // Волна имён из названий файлов закрывается сервером ОТДЕЛЬНО от обработки: без её досчёта шаг
      // открывался бы на ростере, где часть карточек ещё «Получаем имя…».
      this.settledKey() === key &&
      this.personsPrefetchedKey() === this.prefetchKey() &&
      // Снимок сервера не знает о файлах, что эта вкладка ещё грузит: без них сверка откроется неполной.
      (this.pendingUploads?.() ?? 0) === 0
    );
  });

  /** Сервер ещё считает — единственный честный ответ «распознавание идёт»: снимок пришёл и он не
   *  финальный. Клиентские заборы (мутация персон, досчёт волн, догрузка ростера) сюда НЕ входят:
   *  забор мутации персон снимается только новым циклом, а слияние или перенос кадра цикла не рождает,
   *  и `ready` после них остаётся ложным навсегда. Пустой снимок держит последний вердикт: перезапуск
   *  опроса (загрузка, удаление кадра) иначе давал ложный фронт «пошло заново» и снова открывал
   *  закрытую карточку ожидания. Без прежнего вердикта (первый опрос) — «неизвестно», то есть false. */
  private readonly pendingVerdict = signal(false);
  readonly recognitionPending: Signal<boolean> = this.pendingVerdict.asReadonly();

  // Прогрессом считается любое наблюдаемое изменение снимка — счётчик, общее число файлов, статус,
  // цикл. Пока меняется хоть что-то, ожидание нормальное, сколько бы оно ни длилось.
  private readonly stalled = computed(() => {
    this.tick();
    const stamp = this.progressStamp();
    return stamp > 0 && Date.now() - stamp > STALL_MS;
  });

  readonly gate = computed<ProcessingGateView | null>(() => {
    if (!this.projectId()) return null;
    const status = this.status();
    // Флаг добавляется только когда он взведён: здоровое состояние остаётся ровно тем же объектом,
    // что и раньше, и его форму не приходится переучивать ни потребителям, ни тестам.
    // 100 belongs to the ready answer alone: a held 100 after a late restart would read «Распознаём лица 100 %».
    const wait = {
      progress: Math.floor(Math.min(this.shownProgress(), TAIL_CAP)),
      ...(this.stalled() ? { stalled: true as const } : {}),
      ...(this.progressKnown() ? {} : { indeterminate: true as const }),
    };
    // Пустой снимок — тоже ожидание: простой считается от неизменности ответа, а не от его содержимого,
    // и без подсказки эта ветка молчала бы бесконечно. Шаг она не открывает: ready() без снимка ложен.
    if (!status) return { state: 'recognizing', ...wait };
    // Распознавание, а не обработка целиком: снимок остаётся нетерминальным, пока доделываются
    // средние и большие версии, а людей ждать до них незачем — эта ветка решает раньше ready().
    if (!isFacePathDone(status)) return { state: 'recognizing', ...wait };
    if (isClusteringPending(status)) return { state: 'clustering', ...wait };
    if (!this.ready()) return { state: 'clustering', ...wait };
    return { state: 'ready', progress: 100 };
  });

  start(projectId: string, options?: { pendingUploads?: Signal<number> }): void {
    this.pendingUploads = options?.pendingUploads ?? null;
    if (this.pollSub && this.projectId() === projectId) return;
    const projectChanged = this.projectId() !== projectId;
    this.stop();
    if (projectChanged) {
      this.awaitingCycleAdvanceFrom.set(undefined);
      this.fenceByUpload = false;
      this.lastObservedCycleId = null;
      this.pendingVerdict.set(false);
      this.shownProgress.set(0);
      this.progressKnown.set(false);
      this.progressMayDrop = false;
      this.progressAdopt = true;
      this.progressCycle = null;
      this.progressTotal = 0;
      this.tailStartedAt = null;
    }

    this.observedMutationVersion = this.facade.personsMutationVersionFor(projectId)();
    this.projectId.set(projectId);
    this.status.set(null);
    this.lastProgressFingerprint = null;
    // Часы простоя идут с открытия шага, а не с первого долетевшего ответа: иначе 503 или зависший
    // запрос с первого же опроса держали бы спиннер без подсказки бесконечно. Первый снимок
    // переставляет штамп (stampProgress), так что здоровая загрузка от этого не страдает.
    this.progressStamp.set(Date.now());
    this.lastFreshnessKey = null;
    this.resetPrefetch();
    this.armReseedFromStorage(projectId);
    this.startPolling(projectId);
    // Подписка живёт в сервисе, а не в странице мастера: тот же сервис поднимают оба входа в мастер,
    // и в компоненте это пришлось бы писать дважды. Перезапуск опроса — без забора дозагрузки:
    // забор ставит refresh(), и он задержал бы «Продолжить» ровно на то время, ради которого всё это.
    this.stopWatchingCompletion = this.coordinator.watchPhotoSync(projectId, event => {
      if (this.projectId() !== projectId) return;
      if (event.kind === 'project-completed') this.restartPolling(projectId);
      // Принятый аплоад — событие фото, не мутация персон: забор тот же, что у конца загрузки, со сроком.
      if (event.kind === 'upload-accepted') this.onUploadAccepted();
    });
  }

  /** Пересбор персон запущен: заслонка встаёт и переживает перезагрузку через sessionStorage. */
  startReseed(projectId: string): void {
    this.writeStorage(reseedStorageKey(projectId), String(Date.now()));
    this.armReseed(projectId, RESEED_FENCE_MAX_MS);
  }

  /** Заслонка снята (запрос не ушёл или ростер с учениками пришёл). Срок сам её не снимает. */
  clearReseed(projectId: string): void {
    this.reseedSub?.unsubscribe();
    this.reseedSub = null;
    this.reseedFenceState.set(false);
    this.reseedOverdueState.set(false);
    this.dropStorage(reseedStorageKey(projectId));
    this.dropStorage(reseedOverdueStorageKey(projectId));
  }

  /** Запрос пересбора не ушёл: щеколда переживает перезагрузку и даёт ровно один повтор. */
  armReseedRetry(projectId: string): void {
    // Память вкладки — запасное место: без хранилища отложенный пересбор (распознавание ещё шло) иначе
    // не ушёл бы никогда и молча — заслонка досидела бы срок и ушла в алерт «обновите страницу».
    reseedRetryLatch.add(projectId);
    this.writeStorage(reseedRetryStorageKey(projectId), '1');
  }

  /** Снимает щеколду и говорит, была ли она взведена: повтор уходит ровно один раз на заход. */
  takeReseedRetry(projectId: string): boolean {
    const armed: boolean = reseedRetryLatch.has(projectId) || this.readStorage(reseedRetryStorageKey(projectId)) !== null;
    reseedRetryLatch.delete(projectId);
    this.dropStorage(reseedRetryStorageKey(projectId));
    return armed;
  }

  private armReseedFromStorage(projectId: string): void {
    const startedAt = Number(this.readStorage(reseedStorageKey(projectId)));
    const remaining: number = startedAt > 0 ? startedAt + RESEED_FENCE_MAX_MS - Date.now() : 0;
    if (remaining > 0) {
      this.armReseed(projectId, remaining);
      return;
    }

    this.dropStorage(reseedStorageKey(projectId));
    // Обновление страницы — ровно то, о чём просит алерт: заслонка и алерт встают снова, экран честно
    // ждёт ростер с учениками. Щеколда повтора тут НЕ взводится: второй запрос стёр бы персон, которых
    // первый пересбор, возможно, уже заводит.
    if (this.readStorage(reseedOverdueStorageKey(projectId)) !== null) {
      this.reseedFenceState.set(true);
      this.reseedOverdueState.set(true);
    }
  }

  private armReseed(projectId: string, remainingMs: number): void {
    this.reseedSub?.unsubscribe();
    this.reseedFenceState.set(true);
    this.reseedOverdueState.set(false);
    this.dropStorage(reseedOverdueStorageKey(projectId));
    this.reseedSub = timer(remainingMs).subscribe(() => this.markReseedOverdue(projectId));
  }

  /** Срок вышел: таймер уходит, заслонка остаётся, а метка срока меняется на метку «дольше обычного» —
   *  перезагрузка по просьбе алерта поднимает и заслонку, и сам алерт. */
  private markReseedOverdue(projectId: string): void {
    this.reseedSub = null;
    this.dropStorage(reseedStorageKey(projectId));
    this.writeStorage(reseedOverdueStorageKey(projectId), '1');
    this.reseedOverdueState.set(true);
  }

  // sessionStorage может быть закрыт политикой браузера (SecurityError на самом обращении): заслонка тогда
  // живёт только в памяти вкладки и перезагрузку не переживает — но мастер не падает на старте.
  private readStorage(key: string): string | null {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private writeStorage(key: string, value: string): void {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Недоступен — метка остаётся в сигналах до перезагрузки.
    }
  }

  private dropStorage(key: string): void {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Нечего снимать.
    }
  }

  private startPolling(projectId: string): void {
    const generation = ++this.pollGeneration;
    // exhaustMap (not switchMap): a status response slower than the poll interval must still LAND —
    // cancelling in-flight polls every tick starves the gate forever once backend latency exceeds 10s.
    // The timeout aborts only a genuinely hung request so the next tick can retry.
    this.pollSub = timer(0, POLL_INTERVAL_MS)
      .pipe(
        exhaustMap(() => {
          this.statusInFlight = true;
          return this.api.getProjectStatus(projectId).pipe(
            timeout(POLL_TIMEOUT_MS),
            catchError(() => EMPTY),
            // Runs on unsubscribe too, synchronously — a restart clears the flag before its own GET starts.
            finalize(() => (this.statusInFlight = false)),
          );
        }),
      )
      .subscribe(status => {
        if (generation === this.pollGeneration && this.projectId() === projectId) this.onStatus(status);
      });
  }

  stop(): void {
    // Coordinator is root-scoped: marks left behind leak into the Photos page and album-editor strip.
    const projectId = this.projectId();
    if (projectId) this.coordinator.markServerFailed(projectId, []);
    this.stopWatchingCompletion?.();
    this.stopWatchingCompletion = null;
    this.pollSub?.unsubscribe();
    this.pollSub = null;
    this.pollGeneration++;
    this.prefetchGeneration++;
    this.prefetchAttemptKey = null;
    this.settleAttemptKey = null;
    // Срок заслонки остаётся в sessionStorage: следующий start() взводит её заново с остатком.
    this.reseedSub?.unsubscribe();
    this.reseedSub = null;
  }

  /**
   * A finished upload changes the backend picture — refresh the status now, not in ≤10s. The first
   * snapshot after it is still the PREVIOUS cycle's (the orchestrator learns about the files through
   * Kafka, plus the status cache): settling the name wave on that terminal snapshot claims it before
   * its faces exist, and the names are lost for good. The same fence as on a persons mutation holds
   * settle until the backend visibly picked the upload up — a new cycle, or the awaited one turning
   * non-terminal (see onStatus) — or, when nothing landed and no cycle ever comes, the fuse
   * CYCLE_FENCE_MAX_MS. A snapshot without a cycle id cannot be fenced: nothing would ever lift it.
   */
  refresh(): void {
    const projectId = this.armUploadFence();
    if (projectId) this.restartPolling(projectId);
  }

  /**
   * Accepts come one per file: a restart per accept aborted every in-flight status GET, and a whole
   * upload went by without a single snapshot. Only the first accept of a poll window restarts, and only
   * while no status GET is in flight; the rest arm the same fence and drop the same settle/prefetch as
   * refresh(), while the running poll keeps ticking and its in-flight response lands.
   */
  private onUploadAccepted(): void {
    const now: number = Date.now();
    if (!this.statusInFlight && now - this.lastAcceptRestartAt >= POLL_INTERVAL_MS) {
      this.lastAcceptRestartAt = now;
      this.refresh();
      return;
    }

    if (this.armUploadFence()) this.resetPrefetch();
  }

  private armUploadFence(): string | null {
    const projectId = this.projectId();
    if (!projectId || !this.pollSub) return null;
    const cycleId: string | null = this.status()?.processingCycleId ?? this.lastObservedCycleId;
    if (this.awaitingCycleAdvanceFrom() === undefined && cycleId !== null) {
      this.awaitingCycleAdvanceFrom.set(cycleId);
      this.fenceDeadline = Date.now() + CYCLE_FENCE_MAX_MS;
    }

    if (this.awaitingCycleAdvanceFrom() !== undefined) this.fenceByUpload = true;
    return projectId;
  }

  /** Перезапуск опроса без забора: решение «ждать ли следующий цикл» вызывающий принимает сам. */
  private restartPolling(projectId: string): void {
    if (!this.pollSub) return;
    this.pollSub.unsubscribe();
    this.pollSub = null;
    this.pollGeneration++;
    this.status.set(null);
    this.resetPrefetch();
    this.startPolling(projectId);
  }

  private stampProgress(status: ProjectProcessStatus | null): void {
    const fingerprint: string = status
      ? [
          status.processingCycleId ?? 'legacy',
          status.status,
          `${status.countProcessedFiles ?? 0}/${status.countFiles ?? 0}`,
          status.countCycleFacePathDone ?? 0,
          status.countCycleProcessedFiles ?? 0,
          status.clusteringProgress?.done ?? 0,
          // Scenes count nothing: only a stage change shows they move.
          status.personsClusteringStatus ?? '',
          status.sceneDetectionStatus ?? '',
          status.clusteringReady ?? '',
        ].join(':')
      : 'null';
    if (fingerprint === this.lastProgressFingerprint) return;
    this.lastProgressFingerprint = fingerprint;
    this.progressStamp.set(Date.now());
  }

  private onStatus(status: ProjectProcessStatus | null): void {
    // A null poll means the orchestrator has no processing record YET (transient right after a mass
    // upload) — keep it null so the gate stays closed until an explicitly terminal status arrives.
    this.status.set(status);
    if (status) this.pendingVerdict.set(!isProcessingGateReady(status));
    this.stampProgress(status);
    const projectId = this.projectId();
    if (projectId && Array.isArray(status?.failedFileIds)) this.coordinator.markServerFailed(projectId, status.failedFileIds);
    if (projectId && status?.processingCycleId !== undefined && status.processingCycleId !== this.lastObservedCycleId) {
      this.facade.invalidatePersons(projectId);
    }

    if (status?.processingCycleId !== undefined) this.lastObservedCycleId = status.processingCycleId;
    const key = this.freshnessKey();
    if (key !== this.lastFreshnessKey) {
      this.lastFreshnessKey = key;
      this.resetPrefetch();
    }

    const awaitedCycle = this.awaitingCycleAdvanceFrom();
    const cycleId = status?.processingCycleId;
    if (
      awaitedCycle !== undefined &&
      ((awaitedCycle === null && cycleId !== undefined) ||
        (awaitedCycle !== null && cycleId !== undefined && cycleId !== awaitedCycle) ||
        // An upload accepted while a cycle is ACTIVE joins that cycle without changing its id, so a
        // cycle-advance fence would never clear. Seeing the awaited cycle still non-terminal is proof
        // the backend picked the work up — its eventual terminal status includes the joined upload
        // (project completion waits for every file child plus a grace period).
        (awaitedCycle !== null && cycleId === awaitedCycle && !isTerminalProcessStatus(status?.status)) ||
        // Бэкенд так и не шевельнулся (ничего не легло, файл принят уже после обработки соседей, слияние
        // цикла не родило) — срок снимает забор, иначе «Продолжить» заперт навсегда.
        (this.fenceDeadline !== null && Date.now() >= this.fenceDeadline))
    ) {
      this.awaitingCycleAdvanceFrom.set(undefined);
    }

    if (this.awaitingCycleAdvanceFrom() === undefined) {
      this.fenceDeadline = null;
      this.fenceByUpload = false;
    }

    if (projectId && key && this.awaitingCycleAdvanceFrom() === undefined && isProcessingGateReady(status)) {
      // Досчёт волн — СТРОГО перед догрузкой персон: иначе в кэш ляжет ростер без имён, и шаг сверки
      // откроется на нём же.
      if (this.settledKey() === key) this.prefetch(projectId, key);
      else this.settleWaves(projectId, key);
    }
  }

  private onPersonsMutation(version: number, removeOnly: boolean): void {
    this.observedMutationVersion = version;
    // Удаление нового цикла не рождает: цикл остаётся прежним и терминальным, и забор «дождись
    // следующего» не снялся бы никогда — гейт запирался бы навсегда, а с ним и «Продолжить». Что
    // осталось от работы, честно расскажет свежий снимок, который берётся тут же ниже.
    if (!removeOnly && this.awaitingCycleAdvanceFrom() === undefined) {
      this.awaitingCycleAdvanceFrom.set(this.status()?.processingCycleId ?? this.lastObservedCycleId);
      this.fenceDeadline = Date.now() + CYCLE_FENCE_MAX_MS;
    }

    this.status.set(null);
    this.lastFreshnessKey = null;
    this.resetPrefetch();
    const projectId = this.projectId();
    if (projectId) this.restartPolling(projectId);
  }

  /** Заставляет сервер применить волну имён ПРЯМО СЕЙЧАС, не дожидаясь его тихого окна: обработка
   *  бывает терминальной раньше, чем закрыт прогон импорта, и без досчёта гейт открывается на
   *  полуготовых именах. Поколение — то же, что у догрузки: сброс ключа свежести обесценивает и её,
   *  и ответ досчёта в пути (async-state: проверка ПОСЛЕ ответа, не до запроса). */
  private settleWaves(projectId: string, key: string): void {
    if (this.settleAttemptKey === key) return;
    this.settleAttemptKey = key;
    const generation = this.prefetchGeneration;
    const current = (): boolean =>
      generation === this.prefetchGeneration && this.projectId() === projectId && this.freshnessKey() === key;
    // 403 здесь штатный: демо-копия заморожена, не владельцу импорт закрыт — ниже он и читается как «нечего».
    this.personImport
      .settleWaves(projectId, { expectedForbidden: true })
      .pipe(timeout(SETTLE_TIMEOUT_MS))
      .subscribe({
        next: (runs: readonly ImportRun[]) => {
          if (!current()) return;
          this.waveOutcome?.noteRuns(runs);
          // Ответ с прогоном волны, который ещё идёт (параллельный автопрогон крона), — не досчёт:
          // открыть шаг на нём значит показать «Получаем имя…». Следующий тик опроса дожимает снова,
          // тот же кап, что и у транспортного отказа.
          const waveStillRunning: boolean = runs.some(
            (run: ImportRun) => !!run.uploadBatchId && !SETTLED_RUN_STATUSES.has(run.status),
          );
          if (waveStillRunning && ++this.settleFailures < SETTLE_ATTEMPT_CAP) {
            this.settleAttemptKey = null;
            return;
          }

          this.openSettled(projectId, key);
        },
        error: (error: unknown) => {
          if (!current()) return;
          // Сервер ответил определённым «нет» (4xx кроме 408/429: импорт выключен — 404, не владелец — 403):
          // досчитывать нечего. Транспортный отказ (в том числе таймаут) ретраится следующим тиком опроса
          // и только после капа перестаёт держать гейт.
          if (isDefiniteOff(error) || ++this.settleFailures >= SETTLE_ATTEMPT_CAP) {
            this.openSettled(projectId, key);
            return;
          }

          this.settleAttemptKey = null;
        },
      });
  }

  private openSettled(projectId: string, key: string): void {
    this.settledKey.set(key);
    this.prefetch(projectId, key);
  }

  private prefetch(projectId: string, freshnessKey: string): void {
    const key: string = this.prefetchKey() ?? freshnessKey;
    // Окно пересбора: живой снимок статуса — SUMMARY без счётчика персон, и пересбор не двигает в нём
    // ни одного поля (цикл тот же, число файлов то же). По неизменному ключу ростер перечитался бы
    // ровно один раз — пустым, и заслонка не снялась бы никогда. Пока окно открыто (60 с, до «дольше
    // обычного»), ростер перечитывается каждым тиком опроса; сброс prefetchAttemptKey обязателен —
    // одного исключения personsPrefetchedKey из раннего выхода мало, догрузку держит и он.
    const reseedWindow: boolean = this.reseedFenceState() && !this.reseedOverdueState();
    if (reseedWindow) this.prefetchAttemptKey = null;
    if (this.prefetchAttemptKey === key || (!reseedWindow && this.personsPrefetchedKey() === key)) return;
    this.prefetchAttemptKey = key;
    // Ростер, прочитанный ДО конца обработки, устарел: распознавание завело персон, а цикл при этом мог
    // не смениться — догрузка входит в активный цикл его же id, и метку свежести никто не снимал. Без
    // пометки ensurePersons молчит, и шаг сверяет имена с ростером со съёмки, которой ещё не было.
    this.facade.invalidatePersons(projectId);
    const generation = ++this.prefetchGeneration;
    void withTimeout(this.facade.prefetchPersonsWithAvatars(projectId), PREFETCH_TIMEOUT_MS).then(
      () => {
        if (
          generation === this.prefetchGeneration &&
          this.pollSub &&
          this.projectId() === projectId &&
          this.prefetchKey() === key
        ) {
          this.personsPrefetchedKey.set(key);
          // Заслонка пересбора: первый ростер с учениками — персоны заведены заново.
          if (this.reseedFenceState() && this.facade.students().length > 0) this.clearReseed(projectId);
        }
      },
      () => {
        if (generation !== this.prefetchGeneration || this.prefetchAttemptKey !== key) return;
        // Транспортный отказ и таймаут ретраятся следующим тиком опроса; после капа шаг открывается
        // на том ростере, что есть, — сам шаг перечитает его через ensurePersons при входе.
        if (++this.prefetchFailures >= PREFETCH_ATTEMPT_CAP) {
          this.personsPrefetchedKey.set(key);
          return;
        }

        this.prefetchAttemptKey = null;
      },
    );
  }

  private resetPrefetch(): void {
    this.prefetchGeneration++;
    this.prefetchAttemptKey = null;
    this.prefetchFailures = 0;
    this.personsPrefetchedKey.set(null);
    this.settleAttemptKey = null;
    this.settleFailures = 0;
    this.settledKey.set(null);
  }

  /** One 1-s tick: the shown percent closes 1/8 of the gap to the target. Monotonic within a cycle;
   *  only a new cycle, a grown total or an upload fence lets it step back. */
  private stepProgress(): void {
    if (this.ready()) {
      this.progressAdopt = false;
      this.progressKnown.set(true);
      this.shownProgress.set(100);
      return;
    }

    const target: number | null = this.progressTarget();
    if (target === null) return;
    this.progressKnown.set(true);
    if (this.progressAdopt) {
      this.progressAdopt = false;
      this.progressMayDrop = false;
      this.shownProgress.set(target);
      return;
    }

    const shown: number = this.shownProgress();
    if (target < shown && !this.progressMayDrop) return;
    const next: number = Math.abs(target - shown) < 0.5 ? target : shown + (target - shown) / SMOOTHING;
    if (next === target) this.progressMayDrop = false;
    this.shownProgress.set(next);
  }

  /** Percent from the live snapshot per the table above; null = nothing to measure, hold the shown value. */
  private progressTarget(): number | null {
    const status = this.status();
    if (!status) return null;
    if (this.awaitingCycleAdvanceFrom() !== undefined) {
      // Upload onto a finished cycle: the snapshot still shows the old cycle, the new work starts at 0. A fence
      // on a running cycle or after a persons merge holds the value: real growth comes through countCycleFiles.
      if (!this.fenceByUpload || !isTerminalProcessStatus(status.status)) return null;
      this.progressMayDrop = true;
      this.tailStartedAt = null;
      return 0;
    }

    const files: number = status.countCycleFiles ?? 0;
    if (files <= 0) return null;
    const total: number = files + Math.max(this.pendingUploads?.() ?? 0, 0);
    const cycle: string | null = status.processingCycleId ?? null;
    if (cycle !== this.progressCycle) this.progressAdopt = true;
    if (cycle !== this.progressCycle || total > this.progressTotal) this.progressMayDrop = true;
    this.progressCycle = cycle;
    this.progressTotal = total;

    const share = (done: number | undefined): number => Math.min((done ?? 0) / total, 1);
    const facesAndVersions: number =
      FACES_END * share(status.countCycleFacePathDone) + (VERSIONS_END - FACES_END) * share(status.countCycleProcessedFiles);
    const persons: number = (PERSONS_END - VERSIONS_END) * this.personsShare(status, facesAndVersions >= VERSIONS_END);
    const base: number = facesAndVersions + persons;
    if (base < PERSONS_END) {
      this.tailStartedAt = null;
      return base;
    }

    this.tailStartedAt ??= Date.now();
    return Math.min(PERSONS_END + (TAIL_PER_SECOND * (Date.now() - this.tailStartedAt)) / 1000, TAIL_CAP);
  }

  private personsShare(status: ProjectProcessStatus, versionsDone: boolean): number {
    // No batch clustering: the persons span collapses once faces and versions are done.
    if (status.useBatchClustering === false) return versionsDone ? 1 : 0;
    if (status.personsClusteringStatus && TERMINAL_CLUSTERING.includes(status.personsClusteringStatus)) return 1;
    const progress = status.clusteringProgress;
    return progress && progress.total > 0 ? Math.min(progress.done / progress.total, 1) : 0;
  }
}
